import type { Article, ArticleCategory, TransitNote, Video } from '../types/content';
import { images } from './images';

export const articleCategories: ArticleCategory[] = ['Timing & Transits', 'Relationships', 'Ritual & Living', 'Vedic Basics'];

// [NEED FROM ABBY: final article copy] — draft placeholder copy below.
export const articles: Article[] = [
  {
    slug: 'why-your-sun-sign-isnt-the-whole-story',
    title: "Why Your Sun Sign Isn't the Whole Story",
    category: 'Vedic Basics',
    excerpt: 'Your sun sign is one point in a chart of twelve houses and nine planets. Here is what the rest of it says.',
    image: images.journalSun,
    imageAlt: 'An open notebook with a hand-drawn chart beside a brass compass',
    readTime: '6 min read',
    body: [
      'Most of us meet astrology through a sun sign: a single word that is meant to describe a whole person. It is a starting point, but it is a little like describing a city by one street.',
      'A full chart maps where every planet sat at the exact moment and place you were born, across twelve houses that each describe an area of life: self, wealth, home, partnership, career and more.',
      'In Vedic astrology, the moon and the rising sign carry as much weight as the sun, often more. Your moon describes your inner world; your rising sign sets the shape of the entire chart.',
      'That is why a true reading needs your birth date, exact time and city. With those three details, the chart becomes specific to you, and so does the timing of what lies ahead.',
    ],
  },
  {
    slug: 'synastry-101',
    title: 'Synastry 101: How Two Charts Speak to Each Other',
    category: 'Relationships',
    excerpt: 'Where you flow, where you clash and how to grow together: the basics of relationship astrology.',
    image: images.journalSynastry,
    imageAlt: "Two people's hands intertwined on a linen tablecloth",
    readTime: '7 min read',
    body: [
      'Synastry is the art of laying one chart over another. Instead of reading a single blueprint, we read the conversation between two.',
      'Some connections feel effortless because one person’s planets fall softly in the other’s chart. Others carry friction that, understood well, becomes the very place a relationship grows.',
      'A synastry reading looks at attraction, communication, values and long-term potential, and at timing: when a relationship is likely to deepen, and when it may be tested.',
      'It requires both people’s birth details, ideally with exact times, so the houses of each chart line up accurately.',
    ],
  },
  {
    slug: 'a-morning-ritual-for-every-season',
    title: 'A Morning Ritual for Every Season',
    category: 'Ritual & Living',
    excerpt: 'Four simple ways to begin the day with intention, shaped by the turning of the year.',
    image: images.journalMorning,
    imageAlt: 'A cup of tea, a lit candle and an open journal by a sunlit window',
    readTime: '5 min read',
    body: [
      'Ritual does not need to be elaborate. A few quiet minutes, repeated, can change the tone of an entire day.',
      'In spring, open the windows and set one clear intention. In summer, rise early and move your body before the heat. In autumn, write down what you are ready to release. In winter, light a candle and sit in stillness before you reach for your phone.',
      'Each season carries its own energy. Moving with it, rather than against it, is one of the simplest ways to live in tune with time.',
    ],
  },
  {
    slug: 'how-to-read-the-season-ahead',
    title: 'How to Read the Season Ahead',
    category: 'Timing & Transits',
    excerpt: 'Transits explained simply, and why a reading every quarter keeps you a step ahead.',
    image: images.duskSky,
    imageAlt: 'Soft pastel dusk sky over desert mountains',
    readTime: '6 min read',
    body: [
      'Your birth chart never changes, but the sky keeps moving. Transits are the current positions of the planets and how they touch the points in your chart.',
      'Some transits pass in days; others shape years. Knowing which are active helps you choose when to push forward and when to rest.',
      'This is why Abby recommends a reading each quarter: often enough to stay prepared, spaced enough for real change to unfold.',
    ],
  },
  {
    slug: 'the-twelve-houses-simply-explained',
    title: 'The Twelve Houses, Simply Explained',
    category: 'Vedic Basics',
    excerpt: 'From self to spirit: a short guide to the twelve areas of life in your chart.',
    image: images.ritual,
    imageAlt: 'Hands with a gold watch lighting a candle on stone',
    readTime: '8 min read',
    body: [
      'Like the twelve hours on a clock, the twelve houses move around the chart in order, each describing an area of life.',
      'The first house is the self. The fourth is home. The seventh is partnership. The tenth is career and status. The twelfth is release and the spiritual life.',
      'Which signs and planets fall in each house tells the story of where your energy naturally flows, and where it asks for attention.',
    ],
  },
  {
    slug: 'setting-intentions-at-the-new-moon',
    title: 'Setting Intentions at the New Moon',
    category: 'Ritual & Living',
    excerpt: 'A quiet monthly practice for beginning again.',
    image: images.hero,
    imageAlt: 'A woman meditating on a stone ledge in the desert at dusk',
    readTime: '4 min read',
    body: [
      'The new moon is a natural beginning: a dark sky, a clean slate.',
      'Sit somewhere quiet, light a candle and write three intentions in the present tense. Keep them short and specific.',
      'Return to them at the full moon to see what has grown.',
    ],
  },
];

// [NEED FROM ABBY: YouTube channel URL and video list]
export const videos: Video[] = [
  { id: 'v1', title: 'What Vedic Astrology Really Is', duration: '12:40', image: images.journalSun, imageAlt: 'Notebook with a hand-drawn chart' },
  { id: 'v2', title: 'Reading Your Rising Sign', duration: '9:15', image: images.ritual, imageAlt: 'Hands lighting a candle' },
  { id: 'v3', title: 'Synastry: The Basics', duration: '14:02', image: images.journalSynastry, imageAlt: 'Two hands intertwined' },
  { id: 'v4', title: 'A Morning Ritual with Abby', duration: '7:30', image: images.journalMorning, imageAlt: 'Morning tea and journal by a window' },
];

// [NEED FROM ABBY: current transit notes] — sample structure only.
export const transitNotes: TransitNote[] = [
  {
    window: 'Sample entry · This week',
    title: 'A slower week for communication',
    summary: 'Abby’s short note on what is moving in the sky this week and how to work with it.',
    areas: 'Third House · Communication',
  },
  {
    window: 'Sample entry · This month',
    title: 'Relationships come into focus',
    summary: 'A monthly overview of the transits most likely to be felt, sign by sign.',
    areas: 'Seventh House · Partnership',
  },
  {
    window: 'Sample entry · This season',
    title: 'The quarter ahead',
    summary: 'The longer arc: what the next three months may ask of you, and when to book your seasonal reading.',
    areas: 'Tenth House · Career',
  },
];
